<script>
import { mapState } from "vuex";
import Languages from "@/components/widgets/languages";
import GlobalSearch from "@/components/global-search";
import NotificationsBell from "@/components/notifications-bell";
import { canAccessRegionalMenu, firstRegionalUserPath } from "@/utils/regional-user-permissions";
import { isMenuItemVisible } from "@/utils/menu-map";
import { preferenciasUi, alternarTema, alternarMenuRecolhido } from "@/utils/preferencias-ui";
import { rotuloVersao } from "@/utils/versao";

// Mesmo ponto em que o SCSS troca a sidebar fixa pela gaveta (media-breakpoint-down(md)).
const LARGURA_DESKTOP = 1025;

/**
 * Barra superior (redesign 2026-10):
 *   [recolher menu] | [Como funciona] [busca Ctrl K]  ·····  [sino] [tema] [avatar]
 *
 * O botão de menu tem dois papéis: no desktop recolhe/expande a sidebar
 * (preferência salva, ver utils/preferencias-ui.js); abaixo de 1025px abre a
 * gaveta, como antes (quem manipula a gaveta continua sendo o layout).
 */
export default {
  locales: {
    pt: {
      'Settings': 'Configurações',
      'My Account': 'Minha Conta',
      'My Address': 'Meus Endereços',
      'Bank Account': 'Conta Bancária',
      'My Password': 'Minha Senha',
      'Password Security' : 'Senha de Segurança',
      'Logout': 'Sair',
    },
    es: {
      'Settings': 'Configuraciones',
      'My Account': 'Mi Cuenta',
      'My Address': 'Mi Endereços',
      'Bank Account': 'Cuenta Bancaria',
      'My Password': 'Mi Contraseña',
      'Password Security' : 'Contraseña de Seguridad',
      'Logout': 'Cerrar Sesión',
    }
  },
  components: { Languages, GlobalSearch, NotificationsBell },
  props: {
    user: {
      type: Object,
      required: false,
      default: () => ({})
    },
    isCondensed: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      // Espelha body.sidebar-enable só para alimentar o aria-expanded do
      // botão do menu no mobile. A classe no body continua sendo a fonte de
      // verdade (quem a manipula é o layout main.vue).
      menuOpen: false,
      bodyObserver: null,
      larguraJanela: window.innerWidth,
      preferencias: preferenciasUi,
    };
  },
  computed: {
    ...mapState({
      account: state => state.account
    }),
    desktop() {
      return this.larguraJanela >= LARGURA_DESKTOP;
    },
    menuExpandido() {
      return this.desktop ? !this.preferencias.menuRecolhido : this.menuOpen;
    },
    rotuloBotaoMenu() {
      if (this.desktop) return this.preferencias.menuRecolhido ? "Expandir menu" : "Recolher menu";
      return this.menuOpen ? "Fechar menu de navegação" : "Abrir menu de navegação";
    },
    temaEscuro() {
      return this.preferencias.tema === "dark";
    },
    avatarUrl() {
      const u = this.account.user || {};
      return u.avatar || "https://ui-avatars.com/api/?background=27416E&color=fff&name=" + encodeURIComponent(u.name || "");
    },
    versao() {
      return rotuloVersao();
    },
  },
  // A gaveta do menu é aberta/fechada por vários atores (o botão daqui, o véu
  // do main.vue, o router.afterEach e o tour de boas-vindas, que a abre
  // sozinho no mobile para conseguir destacar um item). Em vez de tentar
  // espelhar cada um deles, observamos o atributo `class` do <body>, que é a
  // fonte de verdade única — assim o aria-expanded nunca mente.
  mounted() {
    this.syncMenuOpen();
    window.addEventListener("resize", this.onResize);

    if (typeof MutationObserver === "undefined") return;

    this.bodyObserver = new MutationObserver(this.syncMenuOpen);
    this.bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    if (this.bodyObserver) {
      this.bodyObserver.disconnect();
      this.bodyObserver = null;
    }
  },
  watch: {
    // O layout main.vue remove body.sidebar-enable a cada navegação
    // (router.afterEach). Sem espelhar isso aqui, o aria-expanded do botão do
    // menu ficaria dessincronizado do estado real da gaveta.
    $route() {
      this.syncMenuOpen();
    },
  },
  methods: {
    onResize() {
      this.larguraJanela = window.innerWidth;
    },
    syncMenuOpen() {
      this.menuOpen = document.body.classList.contains("sidebar-enable");
    },
    canAccess(path) {
      return canAccessRegionalMenu(this.account.user, path);
    },
    menuVisible(key) {
      return isMenuItemVisible(this.account.user, key);
    },
    homePath() {
      return this.canAccess("/") ? "/" : firstRegionalUserPath(this.account.user);
    },
    // "Como funciona" — reabre o tour de boas-vindas (antes ficava em "Rever
    // tutorial", no menu do avatar). O OnboardingModal (montado em
    // router/layouts/main.vue) observa esta flag do store. O tour aponta para
    // os itens do menu lateral, então ele volta a ficar expandido.
    replayOnboarding() {
      if (this.desktop && this.preferencias.menuRecolhido) alternarMenuRecolhido(false);
      this.$store.commit("account/setOnboardingReplay", true);
    },
    toggleMenu() {
      if (this.desktop) {
        alternarMenuRecolhido();
        return;
      }
      this.$parent.toggleMenu();
      this.$nextTick(this.syncMenuOpen);
    },
    toggleTema() {
      alternarTema();
    },
    toggleRightSidebar() {
      this.$parent.toggleRightSidebar();
    },
  }
};
</script>

<template>
  <header id="page-topbar">
    <div class="navbar-header">
      <div class="st-topbar-left">
        <!--
          O ícone sozinho não dá nome acessível ao controle: o aria-label muda
          conforme o papel (recolher no desktop / gaveta no mobile) e o
          aria-expanded reflete o estado real.
        -->
        <button
          id="vertical-menu-btn"
          class="st-icon-btn"
          type="button"
          data-tour="menu-toggle"
          :aria-label="rotuloBotaoMenu"
          :title="rotuloBotaoMenu"
          aria-controls="sidebar-menu"
          :aria-expanded="menuExpandido ? 'true' : 'false'"
          @click="toggleMenu"
        >
          <i class="bx" :class="desktop ? 'bx-sidebar' : 'bx-menu'" aria-hidden="true"></i>
        </button>

        <!-- No celular a sidebar (com a logo) vira gaveta: a marca aparece aqui. -->
        <router-link :to="homePath()" class="st-topbar-logo d-lg-none" aria-label="Sales Teams — início">
          <img src="@/assets/images/logo-icon.png" alt="" />
        </router-link>

        <span class="st-topbar-divider d-none d-md-block" aria-hidden="true"></span>

        <button type="button" class="st-topbar-link" title="Como funciona" @click="replayOnboarding">
          <i class="bx bx-help-circle" aria-hidden="true"></i>
          <span class="d-none d-md-inline">Como funciona</span>
          <span class="sr-only d-md-none">Como funciona</span>
        </button>

        <GlobalSearch data-tour="busca" @tutorial="replayOnboarding" @tema="toggleTema" />
      </div>

      <div class="st-topbar-right">
        <Languages class="d-none"></Languages>

        <NotificationsBell />

        <button type="button" class="st-icon-btn st-theme-toggle" :aria-pressed="temaEscuro ? 'true' : 'false'"
          :aria-label="temaEscuro ? 'Mudar para o modo claro' : 'Mudar para o modo escuro'"
          :title="temaEscuro ? 'Modo claro' : 'Modo escuro'" @click="toggleTema">
          <i class="bx" :class="temaEscuro ? 'bx-sun' : 'bx-moon'" aria-hidden="true"></i>
        </button>

        <!--
          data-tour: atributo não-declarado como prop cai no elemento raiz do
          b-dropdown (a <div class="dropdown">), que é o alvo que o tour
          destaca ao falar do perfil.
        -->
        <b-dropdown right no-caret variant="link" toggle-class="st-avatar-btn" menu-class="st-user-menu" data-tour="perfil">
          <template v-slot:button-content>
            <span class="sr-only">Menu do perfil de {{ account.user.name }}</span>
            <img class="st-avatar" :src="avatarUrl" alt="" />
          </template>

          <div class="st-user-menu-head">
            <img class="st-avatar st-avatar-lg" :src="avatarUrl" alt="" />
            <div class="min-w-0">
              <strong class="d-block text-truncate">{{ account.user.name }}</strong>
              <small v-if="account.user.email" class="d-block text-truncate text-muted">{{ account.user.email }}</small>
            </div>
          </div>
          <div class="dropdown-divider"></div>

          <router-link v-if="canAccess('/account')" tag="a" to="/account" class="dropdown-item">
            <i class="bx bx-user" aria-hidden="true"></i><span>{{ t('My Account') }}</span>
          </router-link>
          <router-link v-if="menuVisible('meu-cartao')" tag="a" to="/meu-cartao" class="dropdown-item">
            <i class="bx bx-id-card" aria-hidden="true"></i><span>Meu Cartão</span>
          </router-link>
          <router-link v-if="canAccess('/account/indication')" tag="a" to="/account/indication" class="dropdown-item">
            <i class="bx bx-link" aria-hidden="true"></i><span>{{ t('Link de Indicação') }}</span>
          </router-link>
          <router-link v-if="canAccess('/documents')" tag="a" to="/documents" class="dropdown-item">
            <i class="bx bx-file" aria-hidden="true"></i><span>{{ t('Meus Documentos') }}</span>
          </router-link>
          <router-link v-if="canAccess('/contracts')" tag="a" to="/contracts" class="dropdown-item d-none">
            <span>{{ t('Meus Contratos') }}</span>
          </router-link>
          <router-link v-if="canAccess('/account/address')" tag="a" to="/account/address" class="dropdown-item">
            <i class="bx bx-map" aria-hidden="true"></i><span>{{ t('My Address') }}</span>
          </router-link>
          <router-link v-if="canAccess('/account/pix')" tag="a" to="/account/pix" class="dropdown-item d-none">
            <span>{{ t('Meus Dados Bancários') }}</span>
          </router-link>
          <router-link v-if="canAccess('/account/password')" tag="a" to="/account/password" class="dropdown-item">
            <i class="bx bx-lock-alt" aria-hidden="true"></i><span>{{ t('My Password') }}</span>
          </router-link>
          <router-link v-if="canAccess('/account/password/security')" tag="a" to="/account/password/security" class="dropdown-item d-none">
            <span>{{ t('Password Security') }}</span>
          </router-link>
          <div class="dropdown-divider"></div>
          <router-link tag="a" to="/novidades" class="dropdown-item">
            <i class="bx bx-gift" aria-hidden="true"></i><span>Novidades</span>
            <small class="ml-auto text-muted">{{ versao }}</small>
          </router-link>
          <router-link tag="a" to="/logout" class="dropdown-item text-danger">
            <i class="bx bx-log-out" aria-hidden="true"></i><span>{{ t('Logout') }}</span>
          </router-link>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>
